/**
 * Admin -> Settings -> Hujjatlar (docs/13-hujjatlar.md, 2-bo'lim): hujjat turlari ro'yxati (nomi, majburiylik, muddati)
 * va "submitted" to'sig'i. Umumiy Save oqimi bilan saqlanadi (draft). `cdl` va `medical_card` olib tashlanmaydi --
 * OCR va muddat eslatmalari shularga bog'langan. Yangi tur kaliti nomidan hosil bo'ladi (lotin, pastki chiziq).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import type { DocumentType } from "@/features/documents/types";
import { kindLabel } from "@/features/documents/status";
import type { PanelProps } from "./SettingsPage";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Checkbox, Input } from "@/components/ui/field";
import { Toggle } from "@/components/ui/toggle";

const PROTECTED = ["cdl", "medical_card"];

/** Nomdan kalit: "Drug test" -> "drug_test" (backend tekshiruvi: ^[a-z][a-z0-9_]{0,29}$) */
export function slugKey(label: string, taken: string[]): string {
  const base =
    label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^[^a-z]+/, "")
      .replace(/_+$/, "")
      .slice(0, 30) || "doc";
  let key = base;
  let n = 2;
  while (taken.includes(key) || key === "other") key = `${base.slice(0, 27)}_${n++}`;
  return key;
}

export function DocumentsSettingsPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  const { t: td } = useTranslation("documents");
  const [newLabel, setNewLabel] = useState("");
  const types = draft.document_types ?? [];

  function update(index: number, patch: Partial<DocumentType>) {
    set("document_types", types.map((x, i) => (i === index ? { ...x, ...patch } : x)));
  }

  function add() {
    const label = newLabel.trim();
    if (!label) return;
    set("document_types", [...types, { key: slugKey(label, types.map((x) => x.key)), label, required: false, expires: false }]);
    setNewLabel("");
  }

  return (
    <>
      <Card>
        <CardHeader title={t("documents.title")} subtitle={t("documents.subtitle")} />
        <CardBody>
          <div className="grid items-center gap-x-3 gap-y-2 text-[13px]" style={{ gridTemplateColumns: "minmax(0, 1fr) 150px 110px 140px 36px" }}>
            <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("documents.name")}</div>
            <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("documents.key")}</div>
            <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("documents.required")}</div>
            <div className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">{t("documents.expires")}</div>
            <div />
            {types.map((x, i) => (
              <DocumentTypeRow key={x.key} type={x} onChange={(patch) => update(i, patch)} onRemove={() => set("document_types", types.filter((_, j) => j !== i))} protectedKey={PROTECTED.includes(x.key)} placeholder={kindLabel(td, x.key, x.label)} />
            ))}
          </div>
          <p className="mt-3 text-[12px] text-muted">{t("documents.protectedHint")}</p>
          <div className="mt-4 flex gap-2">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  add();
                }
              }}
              placeholder={t("documents.newType")}
              className="max-w-[320px]"
              maxLength={60}
            />
            <Button onClick={add} disabled={!newLabel.trim()}>
              <Plus size={14} /> {t("documents.add")}
            </Button>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("documents.blockTitle")} />
        <CardBody className="space-y-3">
          <Toggle label={t("documents.block")} hint={t("documents.blockHint")} checked={!!draft.documents_block_submit} onChange={(v) => set("documents_block_submit", v)} />
          <p className="text-[12px] text-muted">{t("documents.ocrNote")}</p>
        </CardBody>
      </Card>
    </>
  );
}

function DocumentTypeRow({ type, onChange, onRemove, protectedKey, placeholder }: { type: DocumentType; onChange: (patch: Partial<DocumentType>) => void; onRemove: () => void; protectedKey: boolean; placeholder: string }) {
  const { t } = useTranslation("settings");
  return (
    <>
      <Input value={type.label} onChange={(e) => onChange({ label: e.target.value })} maxLength={60} placeholder={placeholder} aria-label={t("documents.name")} />
      <code className="truncate text-[12px] text-muted">{type.key}</code>
      <Checkbox checked={type.required} onChange={(e) => onChange({ required: e.target.checked })} aria-label={t("documents.required")} />
      <Checkbox checked={type.expires} onChange={(e) => onChange({ expires: e.target.checked })} aria-label={t("documents.expires")} />
      <button
        type="button"
        onClick={onRemove}
        disabled={protectedKey}
        title={protectedKey ? t("documents.protectedHint") : t("documents.remove", { name: type.label })}
        aria-label={t("documents.remove", { name: type.label })}
        className="grid h-8 w-8 place-items-center rounded-full text-muted hover:bg-fill hover:text-danger disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-muted"
      >
        <X size={14} />
      </button>
    </>
  );
}
